import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Banknote, Timer, ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import RazorpayPay from "@/components/RazorpayPay";
import { SenderVerifyCard } from "@/components/SenderVerify";

/**
 * Payment step shown to the sender once a carrier has accepted and the
 * delivery is in ``awaiting_payment``. Offers all Razorpay methods (card,
 * UPI, netbanking, wallet) plus Cash on Delivery. COD is greyed out with
 * a clear reason when the carrier isn't eligible or has hit their limit.
 */
const PaymentStep = ({ delivery, user, onDone }) => {
  const [codState, setCodState] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    api.get(`/deliveries/${delivery.id}/cod-eligibility`)
      .then((r) => { if (!cancel) setCodState(r.data); })
      // Fallback: fetch the carrier's state via the trips feed if the
      // dedicated endpoint isn't live yet (keeps old builds working).
      .catch(() => {
        if (!cancel) setCodState({ cod_eligible: false, cod_block_reasons: [
          "Checking carrier's cash-on-delivery eligibility…",
        ]});
      });
    return () => { cancel = true; };
  }, [delivery.id]);

  const chooseCod = async () => {
    setBusy(true);
    try {
      await api.post(`/deliveries/${delivery.id}/pay-cod`);
      toast.success("Cash on Delivery confirmed. The carrier will pick up next.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't select COD.");
    } finally { setBusy(false); }
  };

  const codEligible = !!codState?.cod_eligible;
  const codReason = (codState?.cod_block_reasons || [])[0] || "";

  return (
    <div className="rounded-2xl bg-amber-50 border-2 border-amber-200 p-5" data-testid="payment-step">
      <div className="flex items-start gap-2">
        <Timer className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
        <div className="flex-1">
          <div className="font-display font-bold text-amber-900">
            Pay ₹{Number(delivery.sender_total || 0).toFixed(0)} to confirm
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            Your carrier is waiting. You have 15 minutes to pay or the match will be released.
          </div>
        </div>
      </div>

      {user?.sender_verification && !user.sender_verification.can_pay ? (
        <div className="mt-4"><SenderVerifyCard need="pay"/></div>
      ) : (
      <div className="mt-4 space-y-3">
        <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="pay-online-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5">
                <CreditCard className="w-4 h-4 text-amber-600"/> Pay online
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Card, UPI or netbanking via Razorpay. Held safely by CarryIt until drop-off.
              </div>
            </div>
            <RazorpayPay delivery={delivery} user={user} onPaid={onDone}/>
          </div>
          <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-500">
            <ShieldCheck className="w-3 h-3 text-emerald-600"/> CarryIt Protection active
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 ${
            codEligible
              ? "bg-white border-slate-200 hover:border-amber-300 transition"
              : "bg-slate-50 border-slate-200 opacity-80"
          }`}
          data-testid="pay-cod-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5">
                <Banknote className="w-4 h-4 text-amber-600"/> Cash on delivery
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Receiver pays the carrier at drop-off. Keep the exact amount ready.
              </div>
              {!codEligible && codReason && (
                <div
                  data-testid="cod-blocked-reason"
                  className="mt-2 flex items-start gap-1.5 text-xs text-amber-900 bg-amber-100 border border-amber-200 rounded-lg p-2"
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
                  <span>{codReason}</span>
                </div>
              )}
            </div>
            <Button
              onClick={chooseCod}
              disabled={!codEligible || busy}
              data-testid="pay-cod-btn"
              variant={codEligible ? "default" : "outline"}
              className={`rounded-full ${codEligible ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Choose COD"}
            </Button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default PaymentStep;
